import { useEffect, useRef, useState } from 'react';
import AddressBlock from './AddressBlock';
import cartBlackIcon from '../assets/корзина-чёрное.png';
import CartItem from './CartItem';
import { apiClient } from '../api/client';
import { getProductImageUrl } from '../utils/imageUrl';
import type { Product, CartItem as CartItemType } from '../api/types';

interface CartProps {
  userId: string | null;
  onLogoClick?: () => void;
  onCatalogToggle?: () => void;
  isCatalogOpen?: boolean;
  onFavoritesClick?: () => void;
  onCartClick?: () => void;
}

export default function Cart({
  userId,





}: CartProps) {
  const addressRef = useRef<HTMLDivElement>(null);
  const [cartItems, setCartItems] = useState<
    Array<{
      id: string;
      product: Product;
      quantity: number;
      isSelected: boolean;
      isFavorite: boolean;
    }>
  >([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fallbackProductImage = 'https://www.figma.com/api/mcp/asset/227c10fd-7e7e-441e-99fe-d95db76375f0';

  useEffect(() => {
    const loadCart = async () => {
      if (!userId) {
        setCartItems([]);
        setError(null);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const cartResponse = await apiClient.getUserCart(userId);
        if (!cartResponse.success || !cartResponse.data) {
          throw new Error(cartResponse.error || 'Не удалось загрузить корзину');
        }

        const items = cartResponse.data.items || [];
        if (items.length === 0) {
          setCartItems([]);
          setIsLoading(false);
          return;
        }

        const productIds = items.map((item) => item.product_id);
        const productsResponse = await apiClient.getProductsBatch(productIds);
        if (!productsResponse.success || !productsResponse.data) {
          throw new Error(productsResponse.error || 'Не удалось загрузить товары');
        }

        const favoritesResponse = await apiClient.getUserFavorites(userId);
        const favoriteIds = favoritesResponse.success && favoritesResponse.data ? favoritesResponse.data : [];

        const cartItemsWithProducts = items.map((item: CartItemType) => {
          const product = productsResponse.data!.find((p) => p.id === item.product_id);
          if (!product) {
            throw new Error(`Товар ${item.product_id} не найден`);
          }
          return {
            id: item.product_id,
            product,
            quantity: item.quantity,
            isSelected: true,
            isFavorite: favoriteIds.includes(item.product_id),
          };
        });

        setCartItems(cartItemsWithProducts);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Неизвестная ошибка');
        console.error('Error loading cart:', err);
      } finally {
        setIsLoading(false);
      }
    };

    loadCart();
  }, [userId]);

  const selectedItems = cartItems.filter((item) => item.isSelected);
  const orderSummary = {
    itemsCount: selectedItems.length,
    itemsPrice: selectedItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0),
    total: selectedItems.reduce((sum, item) => sum + item.product.price * item.quantity, 0),
  };

  const hasSelectedItems = orderSummary.itemsCount > 0;
  const formatPrice = (price: number) => new Intl.NumberFormat('ru-RU').format(price);

  const handleToggleSelect = (id: string) => {
    setCartItems((items) => items.map((item) => (item.id === id ? { ...item, isSelected: !item.isSelected } : item)));
  };

  const handleToggleFavorite = async (id: string) => {
    if (!userId) return;
    const item = cartItems.find((i) => i.id === id);
    if (!item) return;

    try {
      if (item.isFavorite) {
        const response = await apiClient.removeFromFavorites(userId, id);
        if (!response.success) {
          throw new Error(response.error || 'Не удалось удалить из избранного');
        }
        setCartItems((items) => items.map((i) => (i.id === id ? { ...i, isFavorite: false } : i)));
      } else {
        const response = await apiClient.addToFavorites(userId, id);
        if (!response.success) {
          throw new Error(response.error || 'Не удалось добавить в избранное');
        }
        setCartItems((items) => items.map((i) => (i.id === id ? { ...i, isFavorite: true } : i)));
      }
    } catch (err) {
      console.error('Error toggling favorite:', err);
      alert(err instanceof Error ? err.message : 'Не удалось изменить статус избранного');
    }
  };

  const handleQuantityChange = async (id: string, delta: number) => {
    if (!userId) return;
    const item = cartItems.find((i) => i.id === id);
    if (!item) return;

    const newQuantity = Math.max(1, item.quantity + delta);
    if (newQuantity === item.quantity) return;

    try {
      const response = await apiClient.updateCartItem(userId, id, newQuantity);
      if (!response.success) {
        throw new Error(response.error || 'Не удалось изменить количество');
      }

      setCartItems((items) => items.map((i) => (i.id === id ? { ...i, quantity: newQuantity } : i)));
    } catch (err) {
      console.error('Error updating quantity:', err);
      alert(err instanceof Error ? err.message : 'Не удалось изменить количество');
    }
  };

  const handleDelete = async (id: string) => {
    if (!userId) return;
    try {
      const response = await apiClient.removeFromCart(userId, id);
      if (!response.success) {
        throw new Error(response.error || 'Не удалось удалить товар');
      }

      setCartItems((items) => items.filter((item) => item.id !== id));
    } catch (err) {
      console.error('Error deleting item:', err);
      alert(err instanceof Error ? err.message : 'Не удалось удалить товар');
    }
  };

  return (
    <div className="relative flex-1 bg-[#f3f3f3]">
      <main className="relative mx-auto w-full max-w-[1520px] px-4 pb-[32px] pt-[20px] sm:px-6 lg:px-8">
        <div className="mb-[20px]">
          <div className="flex flex-col gap-5 xl:flex-row xl:items-start xl:justify-between">
            <div className="flex items-center gap-4 pl-1 sm:pl-3 lg:pl-5">
              <img src={cartBlackIcon} alt="Корзина" className="h-[38px] w-[38px] flex-shrink-0 sm:h-[46px] sm:w-[46px] lg:h-[50px] lg:w-[50px]" />
              <h1 className="text-[30px] font-extrabold leading-none tracking-[1px] text-black sm:text-[36px] lg:text-[40px] lg:tracking-[2px]">
                Корзина
              </h1>
            </div>

            {userId && (
              <div ref={addressRef} className="w-full max-w-[360px]">
                <AddressBlock userId={userId} className="h-full" />
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-5 xl:flex-row xl:items-start">
          {userId && (
            <section className="w-full rounded-[20px] bg-white p-4 shadow-[0_6px_18px_rgba(0,0,0,0.05)] sm:p-5 xl:sticky xl:top-5 xl:w-[342px] xl:flex-shrink-0">
              <h2 className="text-[28px] font-extrabold text-black sm:text-[30px]">Заказ</h2>

              <div className="mt-6 flex items-center justify-between gap-4 text-[15px] text-[#353535]">
                <span>Товары ({orderSummary.itemsCount})</span>
                <span className="text-right">{formatPrice(orderSummary.itemsPrice)} <span className="font-bold">₽</span></span>
              </div>

              <div className="my-5 h-[2px] rounded-[20px] bg-[#f3f3f3]" />

              <div className="flex items-end justify-between gap-4">
                <span className="text-[28px] font-extrabold text-black sm:text-[30px]">Итого</span>
                <span className="text-right text-[28px] font-bold text-[#0cce6d] sm:text-[30px]">
                  {formatPrice(orderSummary.total)} ₽
                </span>
              </div>

              <button
                type="button"
                disabled={!hasSelectedItems}
                className={`mt-6 flex h-[48px] w-full items-center justify-center rounded-[18px] text-[18px] font-semibold text-white shadow-[1px_1px_5px_0px_rgba(0,0,0,0.25)] transition-all duration-200 sm:h-[52px] sm:text-[20px] ${
                  hasSelectedItems ? 'bg-[#0cce6d] hover:bg-[#0ab85a] cursor-pointer' : 'bg-[#b4b4b4] cursor-not-allowed'
                }`}
              >
                Оформить заказ
              </button>

              <p className="mt-4 text-center text-[15px] leading-[22px] text-[#747474]">
                {hasSelectedItems
                  ? 'Далее можно выбрать место доставки и способ оплаты'
                  : 'Выберите товары из корзины для оформления заказа'}
              </p>
            </section>
          )}

          {!userId ? (
            <div className="w-full py-16 text-center sm:py-20">
              <p className="text-[18px] text-gray-600 sm:text-[20px]">Корзина пуста</p>
            </div>
          ) : isLoading ? (
            <div className="w-full py-16 text-center sm:py-20">
              <p className="text-[18px] text-gray-600 sm:text-[20px]">Загрузка корзины...</p>
            </div>
          ) : error ? (
            <div className="w-full py-16 text-center sm:py-20">
              <p className="text-[18px] text-red-600 sm:text-[20px]">Ошибка: {error}</p>
            </div>
          ) : cartItems.length === 0 ? (
            <div className="w-full py-16 text-center sm:py-20">
              <p className="text-[18px] text-gray-600 sm:text-[20px]">Корзина пуста</p>
            </div>
          ) : (
            <div className="flex min-w-0 flex-1 flex-col gap-[18px] rounded-[20px] bg-white p-4 shadow-[0_6px_18px_rgba(0,0,0,0.03)] sm:p-5 lg:p-[20px]">
              {cartItems.map((item) => (
                <CartItem
                  key={item.id}
                  id={item.id}
                  image={getProductImageUrl(item.product.primary_image || item.product.primary_thumbnail, fallbackProductImage)}
                  title={item.product.name}
                  price={item.product.price}
                  quantity={item.quantity}
                  isSelected={item.isSelected}
                  isFavorite={item.isFavorite}
                  onToggleSelect={handleToggleSelect}
                  onToggleFavorite={handleToggleFavorite}
                  onDelete={handleDelete}
                  onQuantityChange={handleQuantityChange}
                />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
